import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Skeleton } from '@mui/material';
import clsx from 'clsx';
import { isNumber } from './typeInference';

const PREFIX = 'LoadingImage';

const classes = {
    image: `${PREFIX}image`,
    imageHidden: `${PREFIX}imageHidden`,
    croppedImage: `${PREFIX}croppedImage`
};

const Root = styled('div', {
    shouldForwardProp: prop => prop !== 'loadingImageProps'
})<{ loadingImageProps: LoadingImageProps }>(({ theme, loadingImageProps: props }) => ({
    [`& .${classes.image}`]: {
        width: isNumber(props.width) ? theme.spacing(props.width) : props.width ?? '100%',
        height: isNumber(props.height) ? theme.spacing(props.height) : props.height ?? '100%'
    },

    [`& .${classes.imageHidden}`]: {
        visibility: 'hidden',
        width: 0,
        height: 0,
        padding: 0,
        margin: 0,
        position: 'absolute'
    },

    [`& .${classes.croppedImage}`]: {
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundImage: `url("${props.src}")`
    }
}));

export interface LoadingImageProps {
    height?: number | string;
    width?: number | string;
    src: string;
}

export const LoadingImage: React.FC<React.PropsWithChildren<LoadingImageProps>> = props => {
    const [imageLoaded, setImageLoaded] = React.useState(false);
    const [isError, setIsError] = React.useState(false);
    const image = React.useRef<HTMLImageElement>(null);
    const handleImageLoaded = React.useCallback(() => {
        setImageLoaded(true);
    }, []);
    const handleError = React.useCallback(() => {
        setIsError(true);
    }, []);
    React.useLayoutEffect(() => {
        // Check for cached images, some browsers don't fire the onLoad event if the image is cached
        if (!imageLoaded && image.current?.complete && (image.current?.naturalHeight ?? 0) > 0) {
            handleImageLoaded();
        }
    }, [handleImageLoaded, imageLoaded]);
    if (isError) {
        return null;
    }
    return (
        <Root loadingImageProps={props}>
            <Box
                className={clsx({
                    [classes.image]: imageLoaded,
                    [classes.imageHidden]: !imageLoaded
                })}
                display="flex"
                justifyContent="center"
            >
                <img
                    className={clsx(classes.image, classes.imageHidden)}
                    onLoad={handleImageLoaded}
                    onError={handleError}
                    src={props.src || ''}
                    ref={image}
                />
                <div className={clsx(classes.image, classes.croppedImage)} />
            </Box>
            {!imageLoaded && <Skeleton className={classes.image} variant="rectangular" />}
        </Root>
    );
};
